/* eslint-disable prefer-rest-params */
/* eslint-disable no-console */
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, take, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { PATHS } from '../../../shared/paths';
import { Article, Image } from '../../../shared/types';
import { ErrorLogService } from './error-log.service';
import { Language } from '../../../shared/salesforce-types';
import { LANGUAGE } from '../../../shared/injection-tokens';
import { Page, Section, SubSubsection, SubSection } from 'shared/location-enums';
import { convertSalesforceArticleToArticle, isSalesforceArticleLike, SalesforceArticleLike } from 'shared/article-transform';

export interface ArticleParams {
    tab?: Page;
    section?: Section;
    subSection?: SubSection;
    subSubsection?: SubSubsection;
    order?: number;
    allPages?: boolean;
    nullVersion?: boolean;
    language?: Language;
}

@Injectable({
    providedIn: 'root',
})
export class ArticleService {
    private articles = [] as Article[];

    constructor(private errorLogService: ErrorLogService, private http: HttpClient, @Inject(LANGUAGE) private language: BehaviorSubject<Language>) {}

    fetch(): Observable<Article[]> {
        return this.httpGet().pipe(
            take(1),
            map(articles => articles.map(article => (isSalesforceArticleLike(article) ? convertSalesforceArticleToArticle(article) : article)).filter(article => !!article.tab)),
            tap(articles => {
                this.articles = articles.sort((a, b) => (a.order || 0) - (b.order || 0));
            }),
        );
    }

    getArticles(tab?: Page, section?: Section, subSection?: SubSection, order?: number, allPages?: boolean, nullVersion?: boolean, subSubsection?: SubSubsection): Article[] {
        const articles = this.articles.filter(
            article =>
                (tab === undefined || article.tab === tab) &&
                (section === undefined || article.section === section) &&
                (subSection === undefined || article.subSection === subSection) &&
                (order === undefined || article.order === order) &&
                (allPages === undefined || article.allPages === allPages) &&
                (nullVersion === undefined || article.nullVersion === nullVersion) &&
                (subSubsection === undefined || article.subSubsection === subSubsection) &&
                article.language === this.language.value,
        );

        // Because each KA image has both English and Spanish alt texts, we need to set the current one
        articles.forEach(article => {
            article.images = article.images || [];
            article.images.forEach((image: Image) => {
                let altText = '';
                if (image?.altTextPayload) {
                    if (image.altTextPayload[this.language.value]) {
                        altText = image.altTextPayload[this.language.value];
                    }
                }
                image.altText = altText;
                image.desktopOrMobile = 'desktop';
            });
        });

        return articles.map(article => ({ ...article }));
    }

    getOneDestruct({ tab, section, subSection, order, allPages, nullVersion, subSubsection }: ArticleParams) {
        return this.getOne(tab, section, subSection, order, allPages, nullVersion, subSubsection);
    }

    getOne(tab?: Page, section?: Section, subSection?: SubSection, order?: number, allPages?: boolean, nullVersion?: boolean, subSubsection?: SubSubsection): Article {
        // pull original article list, using filters, if provided
        const allArticles: Article[] = this.getArticles(tab, section, subSection, order, allPages, nullVersion, subSubsection);

        if (allArticles.length === 1) {
            // TODO: check to see if order was set, and if it was, that if it was removed, there would be an issue
            // if (order !== undefined) {
            //     const criteria: string[] = [`language: ${this.language.value}`];
            //
            //     ['tab', 'section', 'subsection', 'order', 'allPages', 'nullVersion', 'language'].forEach((key, index) => {
            //         if (arguments[index] !== undefined) {
            //             criteria.push(`${key}: ${arguments[index]}`);
            //         }
            //     });
            //     const errorMessage = `Knowledge Article uses ORDER in matching criteria:\n${criteria.join('\n')}\n`;
            //
            //     this.errorLogService.log('Order', errorMessage);
            // }

            // TODO: edge case here that we're not coding for.  Could only get one match by removing a single criteria
            // TODO: but could get multiple matches if we remove both

            // Good return
            return allArticles[0];
        } else {
            // Error cases
            const criteria: string[] = [`language: ${this.language.value}`];

            ['tab', 'section', 'subsection', 'order', 'allPages', 'nullVersion', 'subsubsection', 'language'].forEach((key, index) => {
                if (arguments[index] !== undefined) {
                    criteria.push(`${key}: ${arguments[index]}`);
                }
            });

            let errorMessage = '';
            if (allArticles.length === 0) {
                errorMessage = `No Knowledge Article matching criteria:\n${criteria.join('\n')}\n`;
            } else {
                errorMessage = `Multiple (${allArticles.length}) Knowledge Articles matching criteria:\n${criteria.join('\n')}`;
            }

            this.errorLogService.log('KA', errorMessage);

            return {
                title: `!!! DATA ERROR - (${allArticles.length}) MATCHES !!!`,
                bodyText: `!!! DATA ERROR: ${errorMessage.replace(/\n/g, ' | ')} !!!`,
            } as Article;
        }
    }

    private httpGet(): Observable<(Article | SalesforceArticleLike)[]> {
        return this.http.get<(Article | SalesforceArticleLike)[]>(PATHS.articleApi);
    }
}
